import React, { useCallback, useMemo, useState } from "react"
import { Grid, MenuItem, Tooltip } from "@mui/material"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { DeviceMerchantAdmin } from "../../../merchant/admin/merchantAdminDevices/adminDevicesTypes"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { DeviceStatusChip } from "./DeviceDetailsDialogElements"
import { DeviceBase, DeviceTableVariant } from "../deviceTypes"
import { DeviceDetailsDialog } from "./DeviceDetailsDialog"
import { DeviceSupportAdmin } from "../../../support/admin/supportDevicesPage/adminDevicesTypes"
import { DeviceSupplierAdmin } from "../../../supplier/admin/merchant/devices/supplierAdminTypes"
import { LocalizedEnum, LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useColumnFactory } from "../../../../core/api/useTableColumns"
import { useNavigate } from "react-router-dom"
import {
    getDeviceEditRoute,
    SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_CREATE_ROUTE,
    SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_EDIT_ROUTE,
} from "../../../../constants/routes"

type DeviceTableProps = {
    variant: DeviceTableVariant
    ApiQueryTable: (props: any) => JSX.Element
}

export const DeviceTable = ({ variant, ApiQueryTable }: DeviceTableProps) => {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const columnFactoryBase = useColumnFactory<DeviceBase>()
    const columnFactoryMerchant = useColumnFactory<DeviceMerchantAdmin>()
    const columnFactorySupplier = useColumnFactory<DeviceSupplierAdmin>()

    const [dialogDetailsState, setDialogDetailsState] = useState<{
        showDetailsModal: boolean
        deviceId: null | number
    }>({
        showDetailsModal: false,
        deviceId: null,
    })

    const commonColumns: MRT_ColumnDef<DeviceBase>[] = useMemo(
        () => [
            columnFactoryBase.shortNameColumn({
                header: l10n.getString("devices-table-header-type"),
                id: "type",
                accessorFn: (row) => (
                    <LocalizedEnum base="device-details-type-mode" enumValue={row.type}>
                        <span>{row.type}</span>
                    </LocalizedEnum>
                ),
            }),
            columnFactoryBase.typeColumn({
                header: l10n.getString("devices-table-header-status"),
                accessorKey: "statusDetails.status",
                Cell: ({ row }) => (
                    <Tooltip title={row.original.statusDetails.message}>
                        <div>
                            <DeviceStatusChip status={row.original.statusDetails.status} placement="table" />
                        </div>
                    </Tooltip>
                ),
            }),
        ],
        [l10n, columnFactoryBase],
    )

    const commonNameColumn: MRT_ColumnDef<DeviceBase>[] = useMemo(
        () => [
            columnFactoryBase.nameColumn({
                header: l10n.getString("devices-table-header-name"),
                accessorKey: "name",
            }),
        ],
        [l10n, columnFactoryBase],
    )

    const supportTypeColumns: MRT_ColumnDef<DeviceSupportAdmin>[] = useMemo(
        () => [
            {
                header: l10n.getString("support-table-header-supplier"),
                accessorKey: "supplier.name",
                Cell: ({ row }) => {
                    return row.original.merchant?.name ? <span>{row.original.merchant.name}</span> : <span>-</span>
                },
            },
            {
                header: l10n.getString("support-table-header-merchant"),
                accessorKey: "merchant.name",
                Cell: ({ row }) => {
                    return (
                        <>
                            <span>{row.original.merchant.name ?? "-"}</span> <br />
                            <span>{row.original.merchant.addressDisplayText ?? "-"}</span>
                        </>
                    )
                },
            },
        ],
        [l10n],
    )

    const merchantTypeColumns: MRT_ColumnDef<DeviceMerchantAdmin>[] = useMemo(
        () => [
            columnFactoryMerchant.shortNameColumn({
                header: l10n.getString("devices-table-header-group"),
                accessorKey: "deviceGroup.name",
                Cell: ({ row }) => {
                    return row.original.deviceGroup?.name ? (
                        <span>{row.original.deviceGroup.name}</span>
                    ) : (
                        <span>-</span>
                    )
                },
            }),
        ],
        [l10n, columnFactoryMerchant],
    )

    const supplierTypeColumns: MRT_ColumnDef<DeviceSupplierAdmin>[] = useMemo(
        () => [
            columnFactorySupplier.idColumn({
                header: l10n.getString("devices-table-header-reg"),
                accessorKey: "merchant.caseNumber",
                Cell: ({ row }) => {
                    return row.original.merchant?.caseNumber ? (
                        <span>{row.original.merchant.caseNumber}</span>
                    ) : (
                        <span>-</span>
                    )
                },
            }),
            columnFactorySupplier.shortNameColumn({
                header: l10n.getString("devices-table-header-merchant"),
                accessorKey: "merchant.name",
                Cell: ({ row }) => {
                    return row.original.merchant?.name ? <span>{row.original.merchant.name}</span> : <span>-</span>
                },
            }),
        ],
        [l10n, columnFactorySupplier],
    )

    const columns = useMemo(() => {
        switch (variant) {
            case DeviceTableVariant.MERCHANT:
                return [...commonNameColumn, ...merchantTypeColumns, ...commonColumns]
            case DeviceTableVariant.SUPPORT:
                return [...supportTypeColumns, ...commonNameColumn, ...commonColumns]
            case DeviceTableVariant.SUPPLIER:
                return [...supplierTypeColumns, ...commonNameColumn, ...commonColumns]
            case DeviceTableVariant.SERVICE_CENTER:
                return [...commonNameColumn, ...commonColumns]
            default:
                return []
        }
    }, [variant, commonNameColumn, merchantTypeColumns, commonColumns])

    const editServiceDevice = (deviceId: number) => {
        navigate(getDeviceEditRoute(deviceId))
    }

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<DeviceBase>; closeMenu: () => void }) => [
            <MenuItem
                onClick={() => {
                    setDialogDetailsState({ showDetailsModal: true, deviceId: row.original.id })
                    closeMenu()
                }}
                key={`details-${row.original.id}`}
            >
                <LocalizedStrict id="devices-table-button-details">
                    <span>Show details</span>
                </LocalizedStrict>
            </MenuItem>,
            variant === DeviceTableVariant.SERVICE_CENTER && (
                <MenuItem
                    onClick={() => {
                        editServiceDevice(row.original.id)
                        closeMenu()
                    }}
                    key={`edit-${row.original.id}`}
                >
                    <LocalizedStrict id="devices-table-button-edit">
                        <span>Edit</span>
                    </LocalizedStrict>
                </MenuItem>
            ),
        ],
        [setDialogDetailsState, l10n, variant],
    )

    return (
        <>
            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    positionActionsColumn="last"
                    renderRowActionMenuItems={renderRowActionMenuItems}
                />
            </Grid>
            {dialogDetailsState.showDetailsModal && dialogDetailsState.deviceId !== null && (
                <DeviceDetailsDialog
                    deviceId={dialogDetailsState.deviceId}
                    open={dialogDetailsState.showDetailsModal}
                    onClose={() => setDialogDetailsState({ showDetailsModal: false, deviceId: null })}
                />
            )}
        </>
    )
}
